import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import {
  isSettlementExportDownloadable,
  useDownloadSettlementExportMutation,
  useRefetchSettlementExportsOnPusherReconnect,
  useSettlementExports,
} from 'api/settlementExports/settlementExports.api'
import { SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS } from 'api/settlementExports/settlementExports.constants'
import type {
  SettlementExportListWindow,
  SettlementExportRecord,
  SettlementExportStatus,
} from 'api/settlementExports/settlementExports.types'
import { getErrorStatus } from 'helpers/queryClient'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LoadingPlaceholderContainer from 'ui/atoms/LoadingPlaceholderContainer/LoadingPlaceholderContainer'
import NewButton from 'ui/atoms/NewButton/NewButton'
import StatusIcon from 'ui/atoms/StatusIcon/StatusIcon'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import Datetime from 'utils/datetime/datetime'
import { SettlementDayRangePicker } from './SettlementDayRangePicker'
import {
  dayRangeExceedsDays,
  dayRangeToListWindow,
  exportPeriodToDayRange,
  getSettlementExportStatusDetail,
  SETTLEMENT_EXPORT_STATUS_ICONS,
} from './SettlementExportButton.helper'
import type { DayRange } from './SettlementExportButton.types'

type SettlementExportListProps = {
  /** When the listed exports were requested. */
  range: DayRange
  onRangeChange: (range: DayRange) => void
}

export function SettlementExportList({ range, onRangeChange }: SettlementExportListProps): ReactNode {
  const { t } = useTranslation()
  const [isPickerInvalid, setIsPickerInvalid] = useState(false)
  useRefetchSettlementExportsOnPusherReconnect()

  const exceedsMaxWindow = dayRangeExceedsDays(range, SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS)

  return (
    <section className="flex flex-col gap-2">
      <Typography variant="h3">{t(`Settlement exports`)}</Typography>
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="body2" className="text-grey-6">
          {t(`Requested between`)}
        </Typography>
        <SettlementDayRangePicker range={range} onRangeChange={onRangeChange} onValidityChange={setIsPickerInvalid} />
      </div>
      {exceedsMaxWindow ? (
        <InfoBanner
          style="danger"
          text={t(`A search can cover at most {{days}} days. Choose a shorter period.`, {
            days: SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS,
          })}
        />
      ) : (
        !isPickerInvalid && <SettlementExportResults window={dayRangeToListWindow(range)} />
      )}
    </section>
  )
}

type SettlementExportResultsProps = {
  window: SettlementExportListWindow
}

function SettlementExportResults({ window }: SettlementExportResultsProps): ReactNode {
  const { t } = useTranslation()
  const { data: settlementExports, isPending, isError, error } = useSettlementExports(window)

  if (isPending) return <LoadingPlaceholderContainer height={120} />
  if (isError) {
    return (
      <InfoBanner
        style="danger"
        text={
          getErrorStatus(error) === 403
            ? t(`You do not have permission to view settlement exports.`)
            : t(`The settlement exports could not be loaded.`)
        }
      />
    )
  }
  if (settlementExports.length === 0) {
    return <Typography variant="body1">{t(`No settlement exports were requested in this period.`)}</Typography>
  }
  return <SettlementExportTable settlementExports={settlementExports} />
}

type SettlementExportTableProps = {
  settlementExports: SettlementExportRecord[]
}

function SettlementExportTable({ settlementExports }: SettlementExportTableProps): ReactNode {
  const { t } = useTranslation()
  const {
    mutate: download,
    isPending: isDownloading,
    variables: downloadingExport,
  } = useDownloadSettlementExportMutation()

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t(`Period`)}</TableCell>
            <TableCell>{t(`Status`)}</TableCell>
            <TableCell>{t(`Requested`)}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {settlementExports.map((settlementExport) => {
            const { firstDay, lastDay } = exportPeriodToDayRange(settlementExport.start_time, settlementExport.end_time)

            return (
              <TableRow key={settlementExport.id}>
                <TableCell>
                  <div>{firstDay}</div>
                  <div>{lastDay}</div>
                </TableCell>
                <TableCell>
                  <SettlementExportStatusCell settlementExport={settlementExport} />
                </TableCell>
                <TableCell>
                  <div>{Datetime.toLocalTime(Datetime.toISOString(settlementExport.created_at), 'longDay')}</div>
                  <div className="text-grey-6">{settlementExport.creator_name || t(`Unknown`)}</div>
                </TableCell>
                <TableCell align="right">
                  {isSettlementExportDownloadable(settlementExport) && (
                    <NewButton
                      variant="outlined"
                      label={t(`Download`)}
                      icon="fas fa-cloud-download-alt"
                      loading={isDownloading && downloadingExport?.id === settlementExport.id}
                      onClick={() => download(settlementExport)}
                    />
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

type SettlementExportStatusCellProps = {
  settlementExport: SettlementExportRecord
}

function SettlementExportStatusCell({ settlementExport }: SettlementExportStatusCellProps): ReactNode {
  const { t } = useTranslation()

  const statusLabels: Record<SettlementExportStatus, string> = {
    Created: t(`Queued`),
    Processing: t(`Processing`),
    Finished: t(`Finished`),
    Failed: t(`Failed`),
  }
  const statusDetail = getSettlementExportStatusDetail(settlementExport, t)

  return (
    <div className="flex items-start gap-2">
      <StatusIcon variant={SETTLEMENT_EXPORT_STATUS_ICONS[settlementExport.status]} size="small" />
      <div className="flex flex-col">
        <span>{statusLabels[settlementExport.status]}</span>
        {statusDetail && <span className="text-grey-6">{statusDetail}</span>}
      </div>
    </div>
  )
}
